import { useCallback, useMemo } from "react";
import {
  computeShootout,
  type ShootoutOutcome,
  type ShootoutSide,
  type ShootoutState,
} from "../utils/penaltyShootout";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match, Player } from "../types";

interface UseCockpitShootoutParams {
  match: Match | null;
  liveEvents: MatchEvent[];
  queuedEvents: MatchEvent[];
  matchClock: string;
  operatorPeriod: number;
  cockpitLocked: boolean;
  sendEvent: (event: Omit<MatchEvent, "match_id" | "timestamp">) => void;
}

export interface UseCockpitShootoutResult {
  shootout: ShootoutState;
  logShootoutKick: (input: {
    side: ShootoutSide;
    player: Player;
    outcome: ShootoutOutcome;
  }) => void;
}

/**
 * useCockpitShootout — the cockpit's half of the penalty shootout.
 *
 * Reads the standing back out of the event log (never out of local state) so
 * a reload, a second operator, or a correction made in the Review tab all
 * converge on the same tally. See `utils/penaltyShootout.ts` for why a kick
 * is a flagged `SetPiece` rather than a new event type.
 */
export const useCockpitShootout = ({
  match,
  liveEvents,
  queuedEvents,
  matchClock,
  operatorPeriod,
  cockpitLocked,
  sendEvent,
}: UseCockpitShootoutParams): UseCockpitShootoutResult => {
  const shootout = useMemo(
    // Queued (offline) kicks count toward the tally too — the operator must
    // see the kick they just logged even with no connection.
    () => computeShootout(match, [...liveEvents, ...queuedEvents]),
    [match, liveEvents, queuedEvents],
  );

  const logShootoutKick = useCallback(
    ({
      side,
      player,
      outcome,
    }: {
      side: ShootoutSide;
      player: Player;
      outcome: ShootoutOutcome;
    }) => {
      if (!match || cockpitLocked) return;
      const team = side === "home" ? match.home_team : match.away_team;
      sendEvent({
        match_clock: matchClock,
        period: operatorPeriod,
        team_id: team.id,
        player_id: player.id,
        type: "SetPiece",
        data: {
          set_piece_type: "Penalty",
          outcome,
          // The flag every consumer keys off to tell a shootout kick apart
          // from a penalty awarded during play.
          is_shootout: true,
        },
      });
    },
    [cockpitLocked, match, matchClock, operatorPeriod, sendEvent],
  );

  return { shootout, logShootoutKick };
};
